// src/redux/branding/facade.ts
import { useCallback, useEffect } from 'react';
import { unwrapResult } from '@reduxjs/toolkit';

import { fetchSiteConfigAction } from './actions';
import {
    brandingNameSelector,
    brandingLogoUrlSelector,
    brandingPrimarySelector,
    brandingSecondarySelector,
    brandingCurrencyPrefixSelector,
    brandingLoadedSelector,
    brandingErrorSelector,
} from './selectors';
import { useAppDispatch, useAppSelector } from '../hooks';
import { AppDispatch } from '../store';
import { applyBrandTokens, removeBrandTokens, writeCachedBranding, readCachedBranding } from '../../utils/branding';
import { reportError } from '../../services/monitoring';

// Number of live branding consumers. Brand tokens live on :root and must
// survive storefront screen→screen navigation (Ionic mounts the entering page
// before unmounting the leaving one), so we only tear them down once the LAST
// consumer unmounts — a real teardown, not an intermediate revalidation (F21).
let mountedConsumers = 0;

export const useStorefrontBranding = () => {
    const dispatch: AppDispatch = useAppDispatch();

    const name = useAppSelector(brandingNameSelector);
    const logoUrl = useAppSelector(brandingLogoUrlSelector);
    const primary = useAppSelector(brandingPrimarySelector);
    const secondary = useAppSelector(brandingSecondarySelector);
    const currencyPrefix = useAppSelector(brandingCurrencyPrefixSelector);
    const loaded = useAppSelector(brandingLoadedSelector);
    const error = useAppSelector(brandingErrorSelector);

    const fetchConfig = useCallback(
        () =>
            dispatch(fetchSiteConfigAction())
                .then(unwrapResult)
                .catch((err: unknown) => {
                    reportError(err, { context: 'useStorefrontBranding' });
                }),
        [dispatch]
    );

    // Trigger the config fetch on mount. The thunk's fetch-once condition
    // collapses the many consumers/navigations into a single GET.
    useEffect(() => {
        fetchConfig();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Track live consumers; remove brand tokens ONLY when the last one unmounts
    // (real teardown), never on an intermediate navigation unmount.
    useEffect(() => {
        mountedConsumers += 1;
        return () => {
            mountedConsumers -= 1;
            if (mountedConsumers <= 0) {
                mountedConsumers = 0;
                removeBrandTokens();
            }
        };
    }, []);

    // Apply CSS variables when the config resolves.
    useEffect(() => {
        if (!loaded) return;
        if (error) {
            // Fetch failed: keep the correct per-host cached brand rather than
            // painting the default Swool theme over it (F22). On a first visit
            // (no cache) leave the neutral splash alone — do not write defaults.
            const cached = readCachedBranding();
            if (cached) {
                applyBrandTokens(cached.primary, cached.secondary);
            }
            return;
        }
        applyBrandTokens(primary, secondary);
        // Persist a successful config for this host so the next visit applies it
        // synchronously at bootstrap (no default-theme flash).
        writeCachedBranding({ primary, secondary, name, logoUrl, currencyPrefix });
    }, [loaded, error, primary, secondary, name, logoUrl, currencyPrefix]);

    return {
        name,
        logoUrl,
        currencyPrefix,
        primary,
        secondary,
        loaded,
        error,
    } as const;
};
